/* Executive Dashboard for Habita — FASE 3A
 *
 * Single-page SaaS dashboard (Stripe / Linear / Vercel-inspired) that
 * consumes ONE aggregated backend endpoint `/api/admin/dashboard/overview`.
 *
 * Layout: 11 sections, admin-only. Colors: Habita green + white + orange
 * accent. Uses Recharts for charts, shadcn primitives, lucide icons.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, CartesianGrid,
} from 'recharts';
import {
  Users, UserCheck, Building2, Building, Home as HomeIcon, BadgeCheck,
  TrendingUp, TrendingDown, ArrowUpRight, Activity, MousePointerClick,
  Megaphone, Heart, MessageCircle, MapPin, Rocket, ExternalLink,
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, Sparkles,
} from 'lucide-react';

const HABITA_GREEN = '#00824D';
const HABITA_GREEN_DARK = '#005e37';
const HABITA_ORANGE = '#f59e0b';

// ─────────────────────────────────────── Tiny reusable UI atoms

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-gray-100 rounded ${className}`} />;
}

function Section({ title, subtitle, right, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-habita font-bold">{title}</h2>
          {subtitle && <p className="text-sm text-gray-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function KpiCard({ icon: Icon, label, value, deltaPct, testid }) {
  const positive = deltaPct >= 0;
  const nf = new Intl.NumberFormat('es-CO');
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-habita/40 transition-colors shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="w-9 h-9 rounded-xl bg-habita/10 text-habita flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        {typeof deltaPct === 'number' && (
          <span
            className={`text-xs font-semibold flex items-center gap-1 px-2 py-1 rounded-full ${
              positive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
            }`}
          >
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(deltaPct).toFixed(1)}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-xs uppercase tracking-widest text-gray-500 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">{nf.format(value)}</p>
      </div>
    </div>
  );
}

function KpiSkeleton() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <Skeleton className="w-9 h-9 rounded-xl" />
      <Skeleton className="h-3 w-20 mt-6" />
      <Skeleton className="h-8 w-24 mt-2" />
    </div>
  );
}

// ─────────────────────────────────────── Section 1 — KPIs

function KpisGrid({ kpis, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dash-kpis-loading">
        {Array.from({ length: 8 }).map((_, i) => <KpiSkeleton key={i} />)}
      </div>
    );
  }
  const cards = [
    { key: 'users', icon: Users },
    { key: 'agents', icon: UserCheck },
    { key: 'agencies', icon: Building2 },
    { key: 'developers', icon: Building },
    { key: 'projects', icon: Rocket },
    { key: 'properties', icon: HomeIcon },
    { key: 'verified_properties', icon: BadgeCheck },
    { key: 'active_users_30d', icon: Activity },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map(({ key, icon }) => {
        const k = kpis[key];
        return (
          <KpiCard
            key={key}
            icon={icon}
            label={k.label}
            value={k.value}
            deltaPct={k.delta_pct}
            testid={`kpi-${key}`}
          />
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────── Section 2 — Traffic

function TrafficSection({ traffic, loading }) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  const stats = [
    { label: 'Visitas hoy', value: traffic.today },
    { label: 'Últimos 7 días', value: traffic.week },
    { label: 'Últimos 30 días', value: traffic.month },
  ];
  return (
    <div className="grid md:grid-cols-4 gap-4">
      <div className="md:col-span-1 space-y-3">
        {stats.map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-xs uppercase tracking-widest text-gray-500">{s.label}</p>
            <p className="text-2xl font-bold mt-1 tabular-nums text-gray-900">
              {new Intl.NumberFormat('es-CO').format(s.value)}
            </p>
          </div>
        ))}
      </div>
      <div className="md:col-span-3 bg-white rounded-2xl border border-gray-100 p-5" data-testid="traffic-chart">
        <p className="text-sm font-medium text-gray-600 mb-4">Evolución diaria (últimos 30 días)</p>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={traffic.daily_series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} width={40} />
              <Tooltip
                formatter={(v) => [v, 'Visitas']}
                labelFormatter={(l) => l}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              />
              <Line type="monotone" dataKey="visits" stroke={HABITA_GREEN} strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Section 3 — Funnel

function FunnelSection({ funnel, loading }) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  const stages = funnel.stages;
  const maxVal = Math.max(...stages.map(s => s.value)) || 1;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="funnel-section">
      <div className="space-y-3">
        {stages.map((s, i) => {
          const width = Math.max((s.value / maxVal) * 100, 6);
          return (
            <div key={s.label} className="flex items-center gap-4" data-testid={`funnel-stage-${i}`}>
              <div className="w-56 shrink-0 text-sm text-gray-700 font-medium">{s.label}</div>
              <div className="flex-1 relative h-10 bg-gray-50 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
                  style={{
                    width: `${width}%`,
                    background: `linear-gradient(90deg, ${HABITA_GREEN}, ${HABITA_GREEN_DARK})`,
                  }}
                >
                  <span className="text-white text-sm font-semibold tabular-nums">
                    {new Intl.NumberFormat('es-CO').format(s.value)}
                  </span>
                </div>
              </div>
              <div className="w-24 shrink-0 text-right text-sm">
                {s.conversion_from_prev !== null ? (
                  s.conversion_from_prev >= 100 ? (
                    <span className="text-emerald-600 tabular-nums">↑ {s.conversion_from_prev.toFixed(1)}%</span>
                  ) : (
                    <span className="text-gray-500 tabular-nums">↓ {s.conversion_from_prev.toFixed(1)}%</span>
                  )
                ) : (
                  <span className="text-xs text-gray-400">base</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-4">Ventana: últimos 30 días.</p>
    </div>
  );
}

// ─────────────────────────────────────── Section 4 — Rankings

function RankingList({ title, items, hrefFn, emptyMsg, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={testid}>
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center">{emptyMsg}</p>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 10).map((it, i) => (
            <li key={it.id} className="flex items-center gap-3 py-1.5">
              <span className="w-5 shrink-0 text-xs font-mono text-gray-400 text-center">{i + 1}</span>
              {it.avatar && <img src={it.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />}
              {it.logo && <img src={it.logo} alt="" className="w-6 h-6 rounded object-cover" />}
              {hrefFn ? (
                <Link to={hrefFn(it)} className="flex-1 min-w-0 text-sm text-gray-800 truncate hover:text-habita">
                  {it.label}
                </Link>
              ) : (
                <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{it.label}</span>
              )}
              <span className="text-xs text-gray-500 truncate max-w-[80px]">{it.sub}</span>
              <span className="text-sm font-semibold text-habita tabular-nums">{it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RankingsSection({ rankings, loading }) {
  if (loading) return <Skeleton className="h-80 w-full" />;
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      <RankingList
        title="Propiedades más vistas"
        items={rankings.properties_most_viewed}
        hrefFn={(it) => `/propiedad/${it.id}`}
        emptyMsg="Sin vistas registradas"
        testid="ranking-properties-viewed"
      />
      <RankingList
        title="Propiedades más guardadas"
        items={rankings.properties_most_favorited}
        hrefFn={(it) => `/propiedad/${it.id}`}
        emptyMsg="Sin favoritos aún"
        testid="ranking-properties-favorited"
      />
      <RankingList
        title="Asesores más vistos"
        items={rankings.agents_most_viewed}
        emptyMsg="Sin vistas de perfil"
        testid="ranking-agents-viewed"
      />
      <RankingList
        title="Asesores más contactados"
        items={rankings.agents_most_contacted}
        emptyMsg="Sin contactos aún"
        testid="ranking-agents-contacted"
      />
      <RankingList
        title="Inmobiliarias con más actividad"
        items={rankings.agencies_most_active}
        hrefFn={(it) => it.slug ? `/inmobiliaria/${it.slug}` : '#'}
        emptyMsg="Sin actividad de inmobiliarias"
        testid="ranking-agencies"
      />
      <RankingList
        title="Constructoras más vistas"
        items={rankings.developers_most_viewed}
        hrefFn={(it) => it.slug ? `/constructora/${it.slug}` : '#'}
        emptyMsg="Sin vistas de constructoras"
        testid="ranking-developers"
      />
      <RankingList
        title="Proyectos más vistos"
        items={rankings.projects_most_viewed}
        hrefFn={(it) => it.slug ? `/proyecto/${it.slug}` : '#'}
        emptyMsg="Sin vistas de proyectos"
        testid="ranking-projects"
      />
    </div>
  );
}

// ─────────────────────────────────────── Section 5 — Map MVP (Cities)

function MapMvpSection({ mapMvp, loading }) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  const rows = mapMvp.top_cities;
  const max = Math.max(...rows.map(r => r.total), 1);
  // Heat color per row: interpolate green intensity
  const heat = (v) => {
    const ratio = v / max;
    const alpha = 0.15 + ratio * 0.75;
    return `rgba(0, 130, 77, ${alpha.toFixed(2)})`;
  };
  return (
    <div className="grid lg:grid-cols-5 gap-4">
      <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-100 p-5" data-testid="map-bars">
        <p className="text-sm font-medium text-gray-600 mb-4">Top 15 ciudades (todas las entidades)</p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <YAxis dataKey="city" type="category" tick={{ fontSize: 11, fill: '#374151' }} width={90} />
              <Tooltip
                formatter={(v, name) => [v, name === 'total' ? 'Total' : name]}
                contentStyle={{ borderRadius: 8, fontSize: 12 }}
              />
              <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                {rows.map((r, i) => <Cell key={i} fill={heat(r.total)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="map-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Ciudad</th>
              <th className="text-right px-3">Prop.</th>
              <th className="text-right px-3">Asesores</th>
              <th className="text-right px-3">Inmob.</th>
              <th className="text-right px-4">Proy.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.city} className="border-t border-gray-100" style={{ background: heat(r.total) }}>
                <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[110px]">{r.city}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.properties}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.agents}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.agencies}</td>
                <td className="px-4 py-2 text-right tabular-nums font-semibold">{r.projects}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Section 6 — Recent Activity

const ACTIVITY_ICON = {
  user: Users, property: HomeIcon, favorite: Heart, contact: MessageCircle,
  lead: MousePointerClick, agent: UserCheck, agency: Building2,
  developer: Building, project: Rocket, event: Activity,
};

function ActivityFeed({ activity, loading }) {
  if (loading) return <Skeleton className="h-64 w-full" />;
  const fmtRelative = (iso) => {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    if (diff < 60_000) return 'hace segundos';
    if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)} min`;
    if (diff < 86_400_000) return `hace ${Math.floor(diff / 3_600_000)} h`;
    return `hace ${Math.floor(diff / 86_400_000)} d`;
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 max-h-[500px] overflow-y-auto" data-testid="activity-feed">
      {activity.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-10">Sin actividad reciente.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {activity.map((ev, i) => {
            const Icon = ACTIVITY_ICON[ev.kind] || Activity;
            return (
              <li key={i} className="py-2.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-habita/10 text-habita flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">{ev.text}</p>
                  <p className="text-xs text-gray-400">{fmtRelative(ev.at)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────── Section 7 — Marketing quick links

function MarketingSection({ marketing, loading }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  const gaId = process.env.REACT_APP_GA_ID || marketing.ga4.measurement_id;
  const clarityId = process.env.REACT_APP_CLARITY_ID || marketing.clarity.project_id;
  const metaId = process.env.REACT_APP_META_PIXEL_ID || marketing.meta_pixel.pixel_id;

  const gaLink = gaId
    ? `https://analytics.google.com/analytics/web/#/p/reports/dashboard`
    : 'https://analytics.google.com';
  const clarityLink = clarityId
    ? `https://clarity.microsoft.com/projects/view/${clarityId}`
    : 'https://clarity.microsoft.com';
  const metaLink = metaId
    ? `https://business.facebook.com/events_manager2/list/pixel/${metaId}/overview`
    : 'https://business.facebook.com/events_manager2';

  const providers = [
    {
      name: 'Google Analytics',
      badge: gaId || 'no configurado',
      color: 'bg-orange-50 border-orange-200 text-orange-800',
      link: gaLink,
      kpis: [
        { label: 'Eventos totales (30d)', value: marketing.analytics_events_30d },
        { label: 'Vistas de contenido (30d)', value: marketing.view_events_30d },
      ],
      cta: 'Abrir GA4',
    },
    {
      name: 'Meta Pixel',
      badge: metaId || 'no configurado',
      color: 'bg-blue-50 border-blue-200 text-blue-800',
      link: metaLink,
      kpis: [
        { label: 'Contactos (30d)', value: marketing.contact_events_30d },
        { label: 'Leads (30d)', value: marketing.lead_events_30d },
      ],
      cta: 'Events Manager',
    },
    {
      name: 'Microsoft Clarity',
      badge: clarityId || 'no configurado',
      color: 'bg-purple-50 border-purple-200 text-purple-800',
      link: clarityLink,
      kpis: [
        { label: 'Grabaciones', value: 'Ver dashboard' },
        { label: 'Heatmaps', value: 'Ver dashboard' },
      ],
      cta: 'Abrir Clarity',
    },
  ];

  return (
    <div className="grid md:grid-cols-3 gap-4">
      {providers.map(p => (
        <div key={p.name} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`marketing-${p.name.toLowerCase().replace(/\s/g, '-')}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">{p.name}</p>
              <span className={`inline-block mt-1 text-[10px] font-mono px-1.5 py-0.5 rounded ${p.color} border`}>
                {p.badge}
              </span>
            </div>
            <a
              href={p.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-habita hover:text-habita-dark"
              data-testid={`marketing-link-${p.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="space-y-2">
            {p.kpis.map((k, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{k.label}</span>
                <span className="font-semibold text-gray-900 tabular-nums">
                  {typeof k.value === 'number' ? new Intl.NumberFormat('es-CO').format(k.value) : k.value}
                </span>
              </div>
            ))}
          </div>
          <a
            href={p.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-1 text-xs font-semibold text-habita hover:bg-habita/5 px-3 py-2 rounded-lg border border-habita/20 transition-colors"
          >
            {p.cta} <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────── Section 8 — Campaigns

function CampaignsSection({ campaigns, loading }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  const items = [
    { label: 'Emails enviados', value: campaigns.emails_sent },
    { label: 'Open Rate', value: `${campaigns.open_rate}%` },
    { label: 'CTR', value: `${campaigns.ctr}%` },
    { label: 'Completaron perfil', value: campaigns.completed_profile_after_click, hint: 'tras clic en email' },
  ];
  return (
    <div className="grid md:grid-cols-4 gap-4">
      {items.map((it, i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={`campaign-kpi-${i}`}>
          <p className="text-xs uppercase tracking-widest text-gray-500">{it.label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">
            {typeof it.value === 'number' ? new Intl.NumberFormat('es-CO').format(it.value) : it.value}
          </p>
          {it.hint && <p className="text-xs text-gray-400 mt-1">{it.hint}</p>}
        </div>
      ))}
      <div className="md:col-span-4 bg-gradient-to-r from-habita to-habita-dark text-white rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="campaign-best">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6" />
          <div>
            <p className="text-xs uppercase tracking-widest opacity-80">Mejor campaña</p>
            <p className="text-lg font-semibold">
              {campaigns.best_campaign ? campaigns.best_campaign.campaign_id : 'Sin datos aún'}
            </p>
          </div>
        </div>
        {campaigns.best_campaign && (
          <div className="flex items-center gap-6 text-sm">
            <span>Enviados: <strong>{campaigns.best_campaign.sent}</strong></span>
            <span>Open Rate: <strong>{campaigns.best_campaign.open_rate}%</strong></span>
            <span>CTR: <strong>{campaigns.best_campaign.ctr}%</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Section 9 — Platform Health

const HEALTH_LABEL = {
  mongodb: 'MongoDB', backend: 'Backend', frontend: 'Frontend',
  cloudinary: 'Cloudinary', resend: 'Resend', ga4: 'Google Analytics 4',
  meta_pixel: 'Meta Pixel', clarity: 'Microsoft Clarity',
  gsc: 'Google Search Console', sitemap: 'Sitemap',
};

function HealthDot({ status }) {
  if (status === 'ok') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (status === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <XCircle className="w-4 h-4 text-rose-500" />;
}

function HealthSection({ health, loading }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  const tooltip = (val) => {
    if (val.status === 'warning' && val.configured === false) return 'Servicio no configurado en este entorno';
    if (val.note) return val.note;
    if (val.latency_ms != null) return `Latencia MongoDB: ${val.latency_ms}ms`;
    return val.status === 'ok' ? 'Operando con normalidad' : 'Revisar configuración';
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="health-section">
      <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(health).map(([key, val]) => (
          <div
            key={key}
            className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-gray-50"
            data-testid={`health-${key}`}
            title={tooltip(val)}
          >
            <HealthDot status={val.status} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{HEALTH_LABEL[key] || key}</p>
              <p className="text-xs text-gray-500 truncate">
                {val.status === 'ok' ? 'Operativo' : val.status === 'warning' ? 'Advertencia' : 'Error'}
                {val.latency_ms != null && ` · ${val.latency_ms}ms`}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Section 10 — Quick Actions

function QuickActions() {
  const actions = [
    { label: 'Publicar propiedad', href: '/publicar', icon: HomeIcon },
    { label: 'Crear proyecto', href: '/admin', icon: Rocket, note: '(vía panel admin)' },
    { label: 'Registrar asesor', href: '/registro?role=agent', icon: UserCheck },
    { label: 'Registrar inmobiliaria', href: '/registro?role=agency', icon: Building2 },
    { label: 'Registrar constructora', href: '/admin', icon: Building, note: '(seed / admin)' },
    { label: 'Enviar campaña', href: '/admin', icon: Megaphone },
    { label: 'Ver Analytics', href: 'https://analytics.google.com', external: true, icon: Activity },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="quick-actions">
      {actions.map((a, i) => {
        const Icon = a.icon;
        const cls = 'group flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-white border border-gray-100 hover:border-habita hover:shadow-sm transition-all text-center';
        const inner = (
          <>
            <div className="w-10 h-10 rounded-full bg-habita/10 text-habita group-hover:bg-habita group-hover:text-white transition-colors flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
            <span className="text-xs font-medium text-gray-800 leading-tight">{a.label}</span>
            {a.note && <span className="text-[10px] text-gray-400">{a.note}</span>}
          </>
        );
        return a.external ? (
          <a key={i} href={a.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`qa-${i}`}>{inner}</a>
        ) : (
          <Link key={i} to={a.href} className={cls} data-testid={`qa-${i}`}>{inner}</Link>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────── Section 11 — Monthly Goals

function GoalsSection({ goals, loading }) {
  if (loading) return <Skeleton className="h-40 w-full" />;
  const color = (pct) => {
    if (pct >= 100) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
    if (pct >= 66) return { bar: 'bg-habita', text: 'text-habita', bg: 'bg-habita/5' };
    if (pct >= 33) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
    return { bar: 'bg-rose-400', text: 'text-rose-700', bg: 'bg-rose-50' };
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="goals-section">
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-4">
        Período: <span className="text-gray-700 font-semibold">{goals.period_label}</span>
      </p>
      <div className="space-y-4">
        {goals.goals.map((g) => {
          const c = color(g.raw_pct);
          return (
            <div key={g.key} data-testid={`goal-${g.key}`}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium text-gray-800">{g.label}</span>
                <span className={`font-mono tabular-nums ${c.text}`}>
                  {g.current} / {g.target}
                  {g.raw_pct >= 100 && <span className="ml-2 text-xs">✓ meta cumplida</span>}
                </span>
              </div>
              <div className={`h-2.5 rounded-full ${c.bg} overflow-hidden`}>
                <div
                  className={`h-full ${c.bar} rounded-full transition-all duration-700`}
                  style={{ width: `${Math.min(g.pct, 100)}%` }}
                />
              </div>
              <div className="text-right text-xs text-gray-400 mt-1 tabular-nums">
                {g.raw_pct.toFixed(1)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Page shell

export default function AdminExecutiveDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      setError(null);
      const d = await api.adminDashboardOverview();
      setData(d);
    } catch (err) {
      setError(err?.message || 'No pudimos cargar el dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const generatedAt = useMemo(() => {
    if (!data?.generated_at) return null;
    try {
      return new Date(data.generated_at).toLocaleTimeString('es-CO', {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return null; }
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-2xl border border-rose-200 p-6 text-center" data-testid="dash-error">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h2 className="font-serif text-xl font-bold text-gray-900 mb-2">No pudimos cargar el dashboard</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="px-4 py-2 bg-habita text-white rounded-full text-sm font-semibold hover:bg-habita-dark"
            data-testid="dash-retry-btn"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-executive-dashboard">
      {/* Sticky header */}
      <div className="bg-gradient-to-br from-habita to-habita-dark text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-90">Habita · Centro de Control</span>
              </div>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.25)]">Dashboard Ejecutivo</h1>
              <p className="text-white/80 text-sm mt-1">
                Todo lo que necesitas saber sobre el crecimiento de la plataforma, en un solo lugar.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {generatedAt && (
                <span className="text-xs text-white/70">Actualizado a las {generatedAt}</span>
              )}
              <a
                href="/admin/growth-v2"
                className="flex items-center gap-2 px-4 py-2 bg-white text-habita-dark hover:bg-emerald-100 rounded-full text-sm font-semibold transition-colors"
                data-testid="link-growth-v2"
              >
                Growth v2 →
              </a>
              <button
                onClick={() => { setRefreshing(true); fetchData(); }}
                disabled={refreshing || loading}
                className="flex items-center gap-2 px-4 py-2 bg-white text-habita-dark hover:bg-emerald-100 rounded-full text-sm font-semibold disabled:opacity-50 transition-colors"
                data-testid="dash-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>
          </div>
          {user && (
            <p className="text-xs text-white/60 mt-3">
              Autenticado como <span className="font-mono">{user.email}</span> · rol {user.role}
            </p>
          )}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <Section title="Resumen General" subtitle="Estado global de la plataforma con variación mensual" testid="section-kpis">
          <KpisGrid kpis={data?.kpis} loading={loading} />
        </Section>

        <Section title="Objetivos del Mes" subtitle="Progreso de las metas de crecimiento del período actual" testid="section-goals">
          <GoalsSection goals={data?.goals} loading={loading} />
        </Section>

        <Section title="Tráfico" subtitle="Basado en eventos internos de analytics_events" testid="section-traffic">
          <TrafficSection traffic={data?.traffic} loading={loading} />
        </Section>

        <Section title="Embudo de Conversión" subtitle="Desde visita hasta propiedad publicada — últimos 30 días" testid="section-funnel">
          <FunnelSection funnel={data?.funnel} loading={loading} />
        </Section>

        <Section title="Rankings — Top 10" subtitle="Los que más movimiento están generando" testid="section-rankings">
          <RankingsSection rankings={data?.rankings} loading={loading} />
        </Section>

        <Section title="Mapa por Ciudades (MVP)" subtitle="Concentración de actividad por ciudad. Próxima fase: mapa Colombia + Panamá interactivo." testid="section-map">
          <MapMvpSection mapMvp={data?.map_mvp} loading={loading} />
        </Section>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-2xl text-habita font-bold mb-4">Actividad Reciente</h2>
            <ActivityFeed activity={data?.activity || []} loading={loading} />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-habita font-bold mb-4">Estado de la Plataforma</h2>
            <HealthSection health={data?.health} loading={loading} />
          </div>
        </div>

        <Section title="Marketing" subtitle="Indicadores internos + accesos rápidos. Sin duplicar datos externos." testid="section-marketing">
          <MarketingSection marketing={data?.marketing} loading={loading} />
        </Section>

        <Section title="Campañas" subtitle="Rendimiento de emails masivos enviados con Resend" testid="section-campaigns">
          <CampaignsSection campaigns={data?.campaigns} loading={loading} />
        </Section>

        <Section title="Acciones Rápidas" subtitle="Atajos para las operaciones más frecuentes" testid="section-actions">
          <QuickActions />
        </Section>

        {data && (
          <p className="text-center text-xs text-gray-400 mt-8">
            Cálculo del dashboard: {data.compute_ms}ms · Fuente: MongoDB + analytics_events · Habita.co
          </p>
        )}
      </div>
    </div>
  );
}
